import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ApolloClientOptions, InMemoryCache } from '@apollo/client/core';
import { ApolloModule, APOLLO_OPTIONS, gql } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist';

import { APOLLO_CUSTOM_CACHE_KEY } from 'app/utils/apolo-storage-helpers';
import { EnvironmentSettingsService } from 'shared/services/environment-settings.service';

export function createApollo(
  httpLink: HttpLink,
  environmentSettingsService: EnvironmentSettingsService
): ApolloClientOptions<any> {
  const cache = new InMemoryCache({
    possibleTypes: {
      LineInterface: [
        'ProductLine',
        'HeaderLine',
        'SystemLine',
        'TemplateLine',
        'CustomLine'
      ]
    }
  });

  cache.writeQuery({
    query: gql`
      query appQuery {
        branches @client
        currentAccount @client
        currentAccountBranch @client
        currentArticle @client
        currentBranch @client
        currentContactPerson @client
        currentCustomer @client
        currentObjekt @client
        offerObject @client
        currentOffer @client
        currentPosition @client
        customPosition @client
        favorites @client
        formReady @client
        groupedMouseoverTexts @client
        importJob @client
        lastVisitedOffer @client
        loading @client
        miniCartPositions @client
        mouseoverTexts @client
        offerPreview @client
        offerUpdate @client
        offerVersions @client
        productSelector @client
        tableUpdate @client
        updatingPrices @client
        valueTexts @client
        looseArticles @client
        offerColumnsVisibility @client
        disablePriceCalculation @client
        currentSftp @client
      }
    `,
    data: {
      branches: [],
      currentAccount: {},
      currentAccountBranch: {},
      currentArticle: {},
      currentBranch: {},
      currentContactPerson: {},
      currentCustomer: {},
      currentObjekt: {},
      offerObject: {},
      currentOffer: {},
      currentPosition: {},
      customPosition: {},
      favorites: {},
      formReady: false,
      groupedMouseoverTexts: {},
      importJob: {},
      lastVisitedOffer: {},
      loading: false,
      miniCartPositions: {},
      mouseoverTexts: {},
      offerPreview: {},
      offerUpdate: {},
      offerVersions: [],
      productSelector: {},
      tableUpdate: false,
      updatingPrices: false,
      valueTexts: {},
      looseArticles: {},
      offerColumnsVisibility: {},
      disablePriceCalculation: null,
      currentSftp: {}
    }
  });

  (async (): Promise<any> => persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    key: APOLLO_CUSTOM_CACHE_KEY
  }))();

  return {
    link: httpLink.create({ uri: environmentSettingsService.settings.gatewayApiUrl }),
    cache,
    // HOTFIX: @apollo/client 3.8 (PR #10567) flipped the effective default of
    // `assumeImmutableResults` to `true` for InMemoryCache, so ObservableQuery now hands
    // components the original (dev-frozen) cache object instead of a cloneDeep() copy. That
    // broke in-place writers like setCustomPropertyValue ("Cannot assign to read only property").
    // Forcing it back to `false` restores the pre-3.8 cloneDeep behavior across all call sites.
    // TODO: remove once the view-model is built immutably (see set-custom-property-value.ts header).
    assumeImmutableResults: false
  };
}

@NgModule({
  exports: [
    ApolloModule, HttpClientModule
  ],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, EnvironmentSettingsService]
    }
  ]
})
export class GraphQLModule { }
